import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InboxContact,
  InboxContactChat,
  InboxContactChatMessage,
  InboxContacts,
  InboxNewMessage,
} from 'src/constants/types';
import { RootState } from 'src/store';
import { InboxState } from './types';

const areScheduledMessagesEqual = (
  previousMessages: InboxContactChatMessage[],
  nextMessages: InboxContactChatMessage[],
) => {
  if (previousMessages === nextMessages) {
    return true;
  }

  if (previousMessages.length !== nextMessages.length) {
    return false;
  }

  for (let index = 0; index < previousMessages.length; index++) {
    const previousMessage = previousMessages[index];
    const nextMessage = nextMessages[index];

    if (
      previousMessage.id !== nextMessage.id ||
      previousMessage.text !== nextMessage.text ||
      previousMessage.status !== nextMessage.status ||
      previousMessage.dateScheduled !== nextMessage.dateScheduled ||
      previousMessage.dateCreated !== nextMessage.dateCreated
    ) {
      return false;
    }
  }

  return true;
};

const initialState: InboxState = {
  contacts: null,
  chat: null,
  scheduledMessages: [],
  newMessage: null,
  hasMoreMessages: true,
  isLoadingMoreMessages: false,
};

export const inboxSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    setContacts: (state, action: PayloadAction<InboxContacts>) => {
      state.contacts = action.payload;
    },
    setChat: (state, action: PayloadAction<InboxContactChat | null>) => {
      state.chat = action.payload;
      if (action.payload) {
        state.hasMoreMessages = action.payload.items.length === 20;
      } else {
        state.hasMoreMessages = true;
      }
    },
    setScheduledMessages: (state, action: PayloadAction<InboxContactChatMessage[]>) => {
      if (areScheduledMessagesEqual(state.scheduledMessages, action.payload)) {
        return;
      }
      state.scheduledMessages = action.payload;
    },
    appendMessages: (state, action: PayloadAction<InboxContactChatMessage[]>) => {
      if (state.chat) {
        const existingIds = new Set(state.chat.items.map(msg => msg.id));
        const newMessages = action.payload.filter(msg => !existingIds.has(msg.id));
        state.chat.items = [...newMessages, ...state.chat.items];
        state.hasMoreMessages = action.payload.length === 20;
      }
    },
    setLoadingMoreMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMoreMessages = action.payload;
    },
    setChatAsRead: (state, action: PayloadAction<string[]>) => {
      if (state.contacts && state.contacts.items) {
        state.contacts.items = state.contacts.items.map((c: InboxContact) => ({
          ...c,
          isChatRead: action.payload.includes(c.contact.id) ? true : c.isChatRead,
        }));
      }
    },
    setMessageAsUnread: (state, action: PayloadAction<string[]>) => {
      if (state.chat) {
        state.chat.items = state.chat.items.map((c: InboxContactChatMessage) => ({
          ...c,
          isRead: action.payload.includes(c.id) ? true : c.isRead,
        }));
      }
    },
    addMessage: (state, action: PayloadAction<InboxNewMessage>) => {
      state.newMessage = action.payload;
    },
  },
});

export const {
  setContacts,
  setChat,
  setScheduledMessages,
  addMessage,
  setChatAsRead,
  setMessageAsUnread,
  appendMessages,
  setLoadingMoreMessages,
} = inboxSlice.actions;

export const getInboxContacts = (state: RootState) => state.inbox.contacts;
export const getInboxChat = (state: RootState) => state.inbox.chat;
export const getInboxScheduledMessages = (state: RootState) => state.inbox.scheduledMessages;
export const getInboxNewMessage = (state: RootState) => state.inbox.newMessage;
export const getHasMoreMessages = (state: RootState) => state.inbox.hasMoreMessages;
export const getIsLoadingMoreMessages = (state: RootState) => state.inbox.isLoadingMoreMessages;

export const inboxReducer = inboxSlice.reducer;
